import type { LoginState, ActionTypes } from './action-types'
import { GET_RESOURCES_SUCCESS, RESOURCE_CHANGE_SUCCESS } from './action-types'

const initialState: LoginState = {
  user: {
    resource: 0,
    water: 0,
    premium: '0',
    nickName: '',
    walletAddress: '',
    wallet_type: 'real' as 'real' | 'virtual',
    opendPlace: [],
    stakedDiamond: [],
    stakedBirds: [],
    miningModule: null,
    miningRequest: 0,
    goldMine: null,
    goldMineRequest: 0,
    uraniumMine: null,
    uraniumMineRequest: 0,
    powerMine: null,
    powerMineRequest: 0,
    withdrawLimit: 0,
    lastWithdraw: null,
    userRef: '',
    referrals: 0,
    earned: 0,
    discord: '',
    withdraws: [],
    wall: 0,
    tokenAmount: {
      ecs: 0,
      usdt: 0,
    },
    ecsTokenAmount: 0,
    pvpMmr: 0,
    mvmWins: 0,
    mvmLosses: 0,
    mvmLongestWinStreak: 0,
    usdt: 0,
    wheat: 0,
    bread: 0,
    wood: 0,
    stone: 0,
    iron: 0,
    level: 0,
    userExp: 0,
    userLvl: 1,
    buildings: {
      bank: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      wall: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      well_1: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      well_2: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      tower: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      hospital: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      support: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      hunting: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
      training: {
        level: 1,
        hp: 1000,
        maxHp: 1000,
      },
    },
    needsEmailBinding: false,
    energy: 100,
    energyUpdatedAt: null,
    allianceTaskCompletionPercent: 100,
  },
}

export function userReducer(
  state = initialState,
  action: ActionTypes,
): LoginState {
  switch (action.type) {
    case GET_RESOURCES_SUCCESS: {
      const { data } = action.payload
      const { user } = { ...state }
      
      // Handle both nested user object and flat structure
      // Backend returns { user: {...}, ... } but reducer expects flat data
      const userData = data.user || data

      // Handle numeric fields - 0 is a valid value, so check for undefined/null
      user.resource = userData.resource !== undefined && userData.resource !== null 
        ? userData.resource 
        : (user.resource !== undefined ? user.resource : 0)
      user.water = userData.water !== undefined && userData.water !== null 
        ? userData.water 
        : (user.water !== undefined ? user.water : 0)
      user.nickName =
        userData.nickName !== undefined && userData.nickName !== null && userData.nickName !== ''
          ? userData.nickName
          : user.nickName
      user.walletAddress = userData.walletAddress ?? user.walletAddress ?? ''
      user.wallet_type = (userData.wallet_type as 'real' | 'virtual' | undefined) ?? user.wallet_type ?? 'real'
      user.realWalletAddress =
        userData.realWalletAddress !== undefined ? userData.realWalletAddress : user.realWalletAddress
      user.premium = userData.premium
      user.opendPlace = userData.opendPlace
      user.stakedDiamond = userData.stakedDiamond
      user.stakedBirds = userData.stakedBirds
      user.miningModule = userData.miningModule
      user.miningRequest = userData.miningRequest
      user.goldMine = userData.goldMine
      user.goldMineRequest = userData.goldMineRequest
      user.uraniumMine = userData.uraniumMine
      user.uraniumMineRequest = userData.uraniumMineRequest
      user.powerMine = userData.powerMine
      user.powerMineRequest = userData.powerMineRequest
      user.withdrawLimit = userData.withdrawLimit
      user.lastWithdraw = userData.lastWithdraw
      user.userRef = userData.userRef
      user.referrals = userData.referrals
      user.earned = userData.earned
      user.discord = userData.discord
      user.withdraws = userData.withdraws
      user.wall = userData.wall
      user.tokenAmount = userData.tokenAmount
      // Handle ecsTokenAmount - 0 is a valid value, so check for undefined/null
      user.ecsTokenAmount = userData.ecsTokenAmount !== undefined && userData.ecsTokenAmount !== null
        ? userData.ecsTokenAmount
        : (user.ecsTokenAmount !== undefined ? user.ecsTokenAmount : 0)
      // Once-per-account free Immediate marker (null = still available)
      user.freeImmediateUsedAt =
        userData.freeImmediateUsedAt !== undefined
          ? userData.freeImmediateUsedAt
          : user.freeImmediateUsedAt
      // Once-per-account free hospital heal marker (null = still available)
      user.freeHealUsedAt =
        userData.freeHealUsedAt !== undefined
          ? userData.freeHealUsedAt
          : user.freeHealUsedAt
      // Once-per-account free blacksmith craft Immediate marker
      user.freeCraftImmediateUsedAt =
        userData.freeCraftImmediateUsedAt !== undefined
          ? userData.freeCraftImmediateUsedAt
          : user.freeCraftImmediateUsedAt
      // Once-per-account free water (well) Immediate marker
      user.freeWaterImmediateUsedAt =
        userData.freeWaterImmediateUsedAt !== undefined
          ? userData.freeWaterImmediateUsedAt
          : user.freeWaterImmediateUsedAt
      // One-time explanation keys already seen (FTUE phase 2)
      user.seenExplanations = Array.isArray(userData.seenExplanations)
        ? userData.seenExplanations
        : user.seenExplanations
      user.pvpMmr =
        userData.pvpMmr !== undefined && userData.pvpMmr !== null
          ? Number(userData.pvpMmr)
          : user.pvpMmr !== undefined && user.pvpMmr !== null
            ? Number(user.pvpMmr)
            : 0
      const numOr = (v: unknown, fallback: number) =>
        v !== undefined && v !== null && Number.isFinite(Number(v)) ? Math.max(0, Math.floor(Number(v))) : fallback
      user.mvmWins = numOr(userData.mvmWins, numOr(user.mvmWins, 0))
      user.mvmLosses = numOr(userData.mvmLosses, numOr(user.mvmLosses, 0))
      user.mvmLongestWinStreak = numOr(
        userData.mvmLongestWinStreak,
        numOr(user.mvmLongestWinStreak, 0),
      )
      user.usdt = userData.usdt ?? user.usdt ?? 0
      user.wheat = userData.wheat ?? user.wheat ?? 0
      user.bread = userData.bread ?? user.bread ?? 0
      user.wood = userData.wood ?? user.wood ?? 0
      user.stone = userData.stone ?? user.stone ?? 0
      user.iron = userData.iron ?? user.iron ?? 0
      user.level = userData.level
      user.userExp = userData.userExp ?? 0
      user.userLvl = userData.userLvl ?? 1
      user.userExpTowardNextLevel = userData.userExpTowardNextLevel ?? userData.userExpInCurrentLevel
      user.userExpRequiredForNextLevel =
        userData.userExpRequiredForNextLevel ??
        userData.userExpToNextLevel ??
        userData.experienceToNextLevel
      user.buildings = userData.buildings
      // Ensure currentAvatar is properly set (can be object with id/imageUrl or null)
      // Handle case where currentAvatar might be { id: null, imageUrl: null } - treat as null
      if (userData.currentAvatar && typeof userData.currentAvatar === 'object') {
        // If it's an object but both id and imageUrl are null/empty, treat as null
        if ((!userData.currentAvatar.id || userData.currentAvatar.id === null) && 
            (!userData.currentAvatar.imageUrl || userData.currentAvatar.imageUrl === null || userData.currentAvatar.imageUrl.trim() === '')) {
          user.currentAvatar = null
        } else {
          user.currentAvatar = userData.currentAvatar
        }
      } else {
        user.currentAvatar = userData.currentAvatar ?? null
      }
      user.purchasedAvatars = userData.purchasedAvatars ?? []
      user.usdt = userData.usdt ?? user.usdt ?? 0
      user.isBlocked = userData.isBlocked ?? false

      user.email =
        userData.email != null && String(userData.email).trim() !== ''
          ? String(userData.email).trim()
          : ''

      const derivedNeedsEmail =
        (userData.wallet_type || 'real') === 'real' &&
        !(userData.email != null && String(userData.email).trim() !== '')
      user.needsEmailBinding =
        userData.needsEmailBinding !== undefined
          ? Boolean(userData.needsEmailBinding)
          : derivedNeedsEmail

      if (userData.createdAt != null && String(userData.createdAt).trim() !== '') {
        user.createdAt = String(userData.createdAt)
      }
      if (userData.energy !== undefined && userData.energy !== null) {
        user.energy = Number(userData.energy)
      }
      if (userData.energyUpdatedAt !== undefined) {
        user.energyUpdatedAt = userData.energyUpdatedAt ?? null
      }
      if (userData.allianceTaskCompletionPercent !== undefined && userData.allianceTaskCompletionPercent !== null) {
        user.allianceTaskCompletionPercent = Number(userData.allianceTaskCompletionPercent)
      }

      // Timed protection shield (purchased/defeat) — null clears an expired/removed shield.
      user.protectionShieldExpiresAt =
        userData.protectionShieldExpiresAt != null ? String(userData.protectionShieldExpiresAt) : null
      user.protectionShieldSource = userData.protectionShieldSource ?? null

      return { user }
    }

    case RESOURCE_CHANGE_SUCCESS: {
      const { data } = action.payload
      const user = { ...state.user }
      
      // Handle both nested user object and flat structure
      const userData = data.user || data

      user.resource = userData.resource !== undefined && userData.resource !== null ? userData.resource : user.resource
      user.water = userData.water !== undefined && userData.water !== null ? userData.water : user.water
      user.nickName = userData.nickName ? userData.nickName : user.nickName
      user.premium = userData.premium ? userData.premium : user.premium
      user.miningModule = userData.miningModule
        ? userData.miningModule
        : user.miningModule
      user.opendPlace = userData.opendPlace ? userData.opendPlace : user.opendPlace
      user.miningRequest = userData.miningRequest
        ? userData.miningRequest
        : user.miningRequest
      user.stakedDiamond = userData.stakedDiamond
        ? userData.stakedDiamond
        : user.stakedDiamond
      user.stakedBirds = userData.stakedBirds ? userData.stakedBirds : user.stakedBirds
      user.goldMine = userData.goldMine ? userData.goldMine : user.goldMine
      user.goldMineRequest = userData.goldMineRequest
        ? userData.goldMineRequest
        : user.goldMineRequest
      user.uraniumMine = userData.uraniumMine ? userData.uraniumMine : user.uraniumMine
      user.uraniumMineRequest = userData.uraniumMineRequest
        ? userData.uraniumMineRequest
        : user.uraniumMineRequest
      user.powerMine = userData.powerMine ? userData.powerMine : user.powerMine
      user.powerMineRequest = userData.powerMineRequest
        ? userData.powerMineRequest
        : user.powerMineRequest
      user.withdrawLimit = userData.withdrawLimit
        ? userData.withdrawLimit
        : user.withdrawLimit
      user.lastWithdraw = userData.lastWithdraw
        ? userData.lastWithdraw
        : user.lastWithdraw
      user.userRef = userData.userRef ? userData.userRef : user.userRef
      user.referrals = userData.referrals ? userData.referrals : user.referrals
      user.earned = userData.earned ? userData.earned : user.earned
      user.discord = userData.discord ? userData.discord : user.discord
      user.withdraws = userData.withdraws ? userData.withdraws : user.withdraws
      user.wall = userData.wall ? userData.wall : user.wall
      user.tokenAmount = userData.tokenAmount ? userData.tokenAmount : user.tokenAmount
      // Handle ecsTokenAmount - 0 is a valid value, so check for undefined/null
      // API often returns "ecs" (e.g. swap endpoints) - map to ecsTokenAmount for consistency
      const ecsFromPayload = userData.ecsTokenAmount ?? userData.ecs
      user.ecsTokenAmount = ecsFromPayload !== undefined && ecsFromPayload !== null
        ? Number(ecsFromPayload)
        : user.ecsTokenAmount
      // Once-per-account free Immediate marker (set optimistically after a free use)
      user.freeImmediateUsedAt =
        userData.freeImmediateUsedAt !== undefined
          ? userData.freeImmediateUsedAt
          : user.freeImmediateUsedAt
      // Once-per-account free hospital heal marker (set optimistically after a free use)
      user.freeHealUsedAt =
        userData.freeHealUsedAt !== undefined
          ? userData.freeHealUsedAt
          : user.freeHealUsedAt
      // Once-per-account free blacksmith craft Immediate marker
      user.freeCraftImmediateUsedAt =
        userData.freeCraftImmediateUsedAt !== undefined
          ? userData.freeCraftImmediateUsedAt
          : user.freeCraftImmediateUsedAt
      // Once-per-account free water (well) Immediate marker
      user.freeWaterImmediateUsedAt =
        userData.freeWaterImmediateUsedAt !== undefined
          ? userData.freeWaterImmediateUsedAt
          : user.freeWaterImmediateUsedAt
      // One-time explanation keys already seen (FTUE phase 2)
      user.seenExplanations = Array.isArray(userData.seenExplanations)
        ? userData.seenExplanations
        : user.seenExplanations
      if (userData.pvpMmr !== undefined && userData.pvpMmr !== null) {
        user.pvpMmr = Number(userData.pvpMmr)
      }
      user.usdt = userData.usdt !== undefined ? userData.usdt : user.usdt
      user.level = userData.level ? userData.level : user.level
      user.userExp = userData.userExp !== undefined ? userData.userExp : user.userExp
      user.userLvl = userData.userLvl !== undefined ? userData.userLvl : user.userLvl
      if (
        userData.userExpTowardNextLevel !== undefined ||
        userData.userExpInCurrentLevel !== undefined
      ) {
        user.userExpTowardNextLevel = userData.userExpTowardNextLevel ?? userData.userExpInCurrentLevel
      }
      if (
        userData.userExpRequiredForNextLevel !== undefined ||
        userData.userExpToNextLevel !== undefined ||
        userData.experienceToNextLevel !== undefined
      ) {
        user.userExpRequiredForNextLevel =
          userData.userExpRequiredForNextLevel ??
          userData.userExpToNextLevel ??
          userData.experienceToNextLevel
      }
      user.buildings = userData.buildings ? userData.buildings : user.buildings
      // Handle currentAvatar - normalize empty objects to null
      if (userData.currentAvatar !== undefined) {
        if (userData.currentAvatar && typeof userData.currentAvatar === 'object') {
          // If it's an object but both id and imageUrl are null/empty, treat as null
          if ((!userData.currentAvatar.id || userData.currentAvatar.id === null) && 
              (!userData.currentAvatar.imageUrl || userData.currentAvatar.imageUrl === null || 
               (typeof userData.currentAvatar.imageUrl === 'string' && userData.currentAvatar.imageUrl.trim() === ''))) {
            user.currentAvatar = null
          } else {
            user.currentAvatar = userData.currentAvatar
          }
        } else {
          user.currentAvatar = userData.currentAvatar
        }
      }
      user.purchasedAvatars = userData.purchasedAvatars !== undefined ? userData.purchasedAvatars : user.purchasedAvatars
      user.wheat = userData.wheat !== undefined && userData.wheat !== null ? userData.wheat : user.wheat
      user.bread = userData.bread !== undefined && userData.bread !== null ? userData.bread : user.bread
      user.wood = userData.wood !== undefined && userData.wood !== null ? userData.wood : user.wood
      user.stone = userData.stone !== undefined && userData.stone !== null ? userData.stone : user.stone
      user.iron = userData.iron !== undefined && userData.iron !== null ? userData.iron : user.iron
      user.isBlocked = userData.isBlocked !== undefined ? userData.isBlocked : user.isBlocked
      if (userData.energy !== undefined && userData.energy !== null) user.energy = Number(userData.energy)
      if (userData.energyUpdatedAt !== undefined) user.energyUpdatedAt = userData.energyUpdatedAt ?? null
      if (userData.allianceTaskCompletionPercent !== undefined && userData.allianceTaskCompletionPercent !== null) {
        user.allianceTaskCompletionPercent = Number(userData.allianceTaskCompletionPercent)
      }
      if (userData.walletAddress !== undefined) user.walletAddress = userData.walletAddress
      if (userData.wallet_type !== undefined) user.wallet_type = userData.wallet_type
      if (userData.realWalletAddress !== undefined) user.realWalletAddress = userData.realWalletAddress
      if (userData.email !== undefined) {
        user.email =
          userData.email != null && String(userData.email).trim() !== ''
            ? String(userData.email).trim()
            : ''
      }
      if (userData.createdAt !== undefined && userData.createdAt != null && String(userData.createdAt).trim() !== '') {
        user.createdAt = String(userData.createdAt)
      }
      return { ...state, user }
    }

    default:
      return { ...state }
  }
}
